/**
 * Grocery configuration - points at the standalone `gloss-grocery` service.
 * Per-environment values come from `config/{local,development,uat,production}.json`,
 * merged with `config/base.json` and injected at build time (see config.js /
 * webpack.config.js).
 *
 * NB: grocery DELIBERATELY uses one shared catalog across all environments —
 * there is a single deployed `gloss-grocery` stage, so `config/{development,uat,
 * production}.json` intentionally omit a grocery override and every environment
 * reads and writes the endpoint set in `base.json`. Unlike the neighbouring
 * `institutions`/`fiskil` blocks (which share a base `url` but split by a
 * per-env `apiStage`), grocery's `apiStage` is kept for config-shape parity only
 * and is NOT used to build request URLs. This was reviewed in #4649: the
 * write-back (`POST /v1/grocery/prices`) means non-prod price submissions land
 * in the same catalog production reads — accepted as intended until a separate
 * per-stage grocery deployment exists.
 */

export interface GroceryConfig {
  url: string;
  apiStage: string;
  endpoint: {
    items: string;
    prices: string;
    lists: string;
  };
}

const groceryConfig = process.env.GROCERY as unknown as GroceryConfig | undefined;

/** Default endpoint paths, applied when the injected config omits one. */
const DEFAULT_ENDPOINTS = {
  items: "/v1/grocery/items",
  prices: "/v1/grocery/prices",
  lists: "/v1/grocery/lists",
};

/**
 * Returns the grocery config with endpoint paths defaulted. The defaults guard
 * against a config bundle injected before a new endpoint existed (e.g. a dev
 * server started before `prices` was added), so a newly-added endpoint works
 * after a code reload without a full rebuild.
 */
export function getGroceryConfig(): GroceryConfig {
  return {
    url: groceryConfig?.url ?? "",
    apiStage: groceryConfig?.apiStage ?? "not-yet-deployed",
    endpoint: {
      items: groceryConfig?.endpoint?.items ?? DEFAULT_ENDPOINTS.items,
      prices: groceryConfig?.endpoint?.prices ?? DEFAULT_ENDPOINTS.prices,
      lists: groceryConfig?.endpoint?.lists ?? DEFAULT_ENDPOINTS.lists,
    },
  };
}
